import { authenticatedApiClient } from '../axios';
import { GROUPS_ENDPOINT, GROUPS_OF_USER_ENDPOINT } from '../config/api';

export interface GroupMeta {
    userIsMember: boolean;
    groupMemberCount: number;
    upcomingGroupEventCount: number;
}

export interface Group {
    uuid: string;
    name: string;
    imageUrl?: string;
    description?: string;
    meta: GroupMeta;
    createdAt: string;
    isDefault?: boolean;
    isMarketplace?: boolean;
    featured?: boolean;
    userIsMember: boolean;
}

export interface GroupsResponse {
    groups: Group[];
}

export interface JoinGroupRequest {
    groupId: string;
    role?: number;
    user?: string
}

export interface SuggestGroupRequest {
    title: string;
    description: string;
    comment?: string;
    suggestingUser: string;
    building: string;
}

/**
 * Groups service for handling group-related operations
 */
class GroupsService {
    /**
     * Get groups for a building
     */
    async getGroups(buildingUuid: string): Promise<Group[]> {
        const config = {
            params: {
                building: [buildingUuid],
                active: true,
            },
        };

        const response = await authenticatedApiClient.get<Group[]>(GROUPS_ENDPOINT, config);
        return response;
    }

    /**
     * Get groups user is a member of
     */
    async getGroupsOfUser(): Promise<Group[]> {
        const response = await authenticatedApiClient.get<Group[]>(GROUPS_OF_USER_ENDPOINT);
        return response;
    }

    /**
     * Get detailed information about a specific group
     */
    async getGroupDetail(groupId: string): Promise<Group> {
        const response = await authenticatedApiClient.get<Group>(`/group/${groupId}`);
        return response;
    }

    /**
     * Get gatherings/events for a specific group
     */
    async getGroupGatherings(groupId: string): Promise<any[]> {

        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        const params = {
            group: groupId,
            sort: "scheduledStartTime ASC",
            startTime: JSON.stringify({ ">": twoMonthsAgo }),
        };
        const response = await authenticatedApiClient.get<any[]>(`/groupevent`, { params: params });
        return response;
    }

    /**
     * Get members of a specific group
     */
    async getGroupMembers(groupId: string): Promise<any[]> {
        const response = await authenticatedApiClient.get<any[]>(`/groupmember`, { params: { group: groupId } });
        return response;
    }

    /**
     * Join a group
     */
    async joinGroup(request: JoinGroupRequest): Promise<any> {
        const response = await authenticatedApiClient.post<any>('/groupmember', {
            group: request.groupId,
            role: request.role || 0,
            user: request.user,
        });
        return response.data;
    }

    /**
     * Leave a group
     */
    async leaveGroup(groupMemberUuid: string): Promise<any> {
        const response = await authenticatedApiClient.patch<any>('/groupmember', { ids: [groupMemberUuid], active: false });
        return response.data;
    }

    /**
     * Suggest a new group
     */
    async suggestGroup(request: SuggestGroupRequest): Promise<any> {
        const response = await authenticatedApiClient.post<any>('/proposedgroup', request);
        return response.data;
    }

    /**
     * Get user's group memberships
     */
    async getUserGroups(): Promise<Group[]> {
        const response = await authenticatedApiClient.get<Group[]>('/user/groups');
        return response;
    }
}

// Export a singleton instance
export const groupsService = new GroupsService();